<template>
  <div class="relative">
    <Head title="Prenota - Conca del Sole" />

    <!-- Header with disabled menu interactions -->
    <div class="pointer-events-none">
      <Header />
    </div>

    <!-- Full-page backdrop to prevent any clicks underneath -->
    <div class="fixed inset-0 bg-gray-900 bg-opacity-60 z-30"></div>

    <div class="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 relative z-40 pt-32 pointer-events-auto">
      <div class="max-w-4xl mx-auto">
        <!-- Close Button -->
        <div class="mb-4 flex justify-end">
          <Link
            :href="returnUrl"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Chiudi
          </Link>
        </div>

        <!-- Progress Steps -->
        <div class="mb-8">
          <div class="flex items-center justify-center">
            <div v-for="(stepItem, index) in steps" :key="index" class="flex items-center">
              <div class="flex flex-col items-center">
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                  :class="currentStep > index ? 'bg-green-500' : currentStep === index ? 'bg-indigo-600' : 'bg-gray-300'"
                >
                  <span v-if="currentStep > index">✓</span>
                  <span v-else>{{ index + 1 }}</span>
                </div>
                <span class="mt-2 text-sm font-medium" :class="currentStep >= index ? 'text-gray-900' : 'text-gray-500'">
                  {{ stepItem }}
                </span>
              </div>
              <div v-if="index < steps.length - 1" class="w-24 h-1 mx-4" :class="currentStep > index ? 'bg-green-500' : 'bg-gray-300'" />
            </div>
          </div>
        </div>

        <!-- Step Content -->
        <div class="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <!-- Step 1: Select Date -->
          <div v-if="currentStep === 0">
            <h2 class="text-2xl font-bold mb-6 text-center">Seleziona la data della visita</h2>
            <div class="max-w-md mx-auto">
              <DatePicker
                v-model="selectedDate"
                class="rounded-lg border w-full"
                borderless expanded
                :min-date="minDate"
                :max-date="maxDate"
                :disabled-dates="disabledDates"
                locale="it"
                mode="date"
              />
              <p v-if="!selectedDate" class="mt-4 text-sm text-red-600 text-center">
                Seleziona una data per continuare
              </p>
            </div>
          </div>

          <!-- Step 2: Choose Option -->
          <div v-if="currentStep === 1">
            <h2 class="text-2xl font-bold mb-2 text-center">Cosa desideri prenotare?</h2>
            <p class="text-center text-gray-600 mb-6">
              Data selezionata: <strong>{{ formatDate(selectedDate) }}</strong>
            </p>

            <!-- Option Selection -->
            <div class="grid md:grid-cols-2 gap-6 mb-6">
              <!-- Option 1: Entrance Only -->
              <div
                class="border-2 rounded-lg p-6 cursor-pointer transition-all"
                :class="bookingType === 'tickets' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'"
                @click="bookingType = 'tickets'"
              >
                <div class="flex items-start">
                  <input
                    type="radio"
                    :checked="bookingType === 'tickets'"
                    class="mt-1 mr-3"
                  />
                  <div class="flex-1">
                    <h3 class="text-lg font-semibold mb-2">Ingressi Piscina</h3>
                    <p class="text-sm text-gray-600 mb-3">Solo biglietto d'ingresso senza lettini</p>
                    <div class="text-sm">
                      <div class="flex justify-between mb-2">
                        <span>Adulti (12+ anni):</span>
                        <span class="font-semibold">€{{ prices.adults }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span>Bambini (3-11 anni):</span>
                        <span class="font-semibold">€{{ prices.kids }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Option 2: Umbrellas -->
              <div
                class="border-2 rounded-lg p-6 cursor-pointer transition-all"
                :class="bookingType === 'umbrellas' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'"
                @click="selectUmbrellas"
              >
                <div class="flex items-start">
                  <input
                    type="radio"
                    :checked="bookingType === 'umbrellas'"
                    class="mt-1 mr-3"
                  />
                  <div class="flex-1">
                    <h3 class="text-lg font-semibold mb-2">Ombrelloni con Lettini</h3>
                    <p class="text-sm text-gray-600 mb-3">Include lettini + 2 ingressi</p>
                    <div class="text-sm">
                      <div class="flex justify-between mb-2">
                        <span>Ombrellone (2 lettini):</span>
                        <span class="font-semibold">€{{ prices.umbrella }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span>Postazione (4-6 lettini):</span>
                        <span class="font-semibold">€{{ prices.station }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tickets Selection (if tickets selected) -->
            <div v-if="bookingType === 'tickets'" class="mt-6 bg-gray-50 rounded-lg p-6">
              <h3 class="font-semibold mb-4">Seleziona quantità</h3>
              <div class="space-y-4">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="font-medium">Adulti</p>
                    <p class="text-sm text-gray-600">Da 12 anni - €{{ prices.adults }}</p>
                  </div>
                  <NumberInput
                    element-id="adults"
                    :value="adultQty"
                    @qty-change="calculateTotalAdults"
                  />
                </div>
                <div class="flex justify-between items-center">
                  <div>
                    <p class="font-medium">Bambini</p>
                    <p class="text-sm text-gray-600">Da 3 a 11 anni - €{{ prices.kids }}</p>
                  </div>
                  <NumberInput
                    element-id="kids"
                    :value="kidsQty"
                    @qty-change="calculateTotalKids"
                  />
                </div>
              </div>
            </div>

            <!-- Pool Map (if umbrellas selected) -->
            <div v-if="bookingType === 'umbrellas'" class="mt-6">
              <h3 class="font-semibold mb-4 text-center">Seleziona i tuoi ombrelloni sulla mappa</h3>
              <div class="rounded-lg border bg-green-100 overflow-hidden">
                <p v-if="Object.keys(busy).length === 94" class="text-sm text-red-500 p-4 text-center">
                  Tutti gli ombrelloni prenotabili online sono esauriti.
                </p>
                <pool-map
                  :busy="busy"
                  :allow-untoggle="false"
                  :show-numbers="!isSafari()"
                  @update-stations="updateStations"
                />
              </div>
            </div>
          </div>

          <!-- Step 3: Review -->
          <div v-if="currentStep === 2">
            <h2 class="text-2xl font-bold mb-6 text-center">Conferma Prenotazione</h2>

            <div class="space-y-4 mb-6">
              <div class="flex justify-between py-3 border-b">
                <span class="font-medium">Data:</span>
                <span>{{ formatDate(selectedDate) }}</span>
              </div>

              <div v-if="adultQty > 0" class="flex justify-between py-3 border-b">
                <span>Adulti x {{ adultQty }}</span>
                <span class="font-semibold">€{{ totalAdults }}</span>
              </div>

              <div v-if="kidsQty > 0" class="flex justify-between py-3 border-b">
                <span>Bambini x {{ kidsQty }}</span>
                <span class="font-semibold">€{{ totalKids }}</span>
              </div>

              <div
                v-for="umbrella in selectedStations"
                :key="umbrella.id"
                class="flex justify-between py-3 border-b"
              >
                <div>
                  <div class="font-medium">Ombrellone {{ umbrella.id }}</div>
                  <div class="text-sm text-gray-600">{{ umbrella.sunbedCount }} lettini + 2 ingressi inclusi</div>
                  <div v-if="umbrella.type === 'station' && (umbrella.extraSunbedCount > 0 || umbrella.extraAdultCount > 0)" class="text-sm text-gray-600">
                    <span v-if="umbrella.extraSunbedCount > 0">Extra lettini: {{ umbrella.extraSunbedCount }}</span>
                    <span v-if="umbrella.extraSunbedCount > 0 && umbrella.extraAdultCount > 0"> • </span>
                    <span v-if="umbrella.extraAdultCount > 0">Extra adulti: {{ umbrella.extraAdultCount }}</span>
                  </div>
                </div>
                <span class="font-semibold">€{{ getStationPrice(umbrella) }}</span>
              </div>

              <div class="flex justify-between py-4 text-xl font-bold border-t-2">
                <span>Totale</span>
                <span class="text-indigo-600">€{{ total }}</span>
              </div>
            </div>

            <!-- Privacy Policy Checkboxes -->
            <div class="space-y-3 mt-6 p-4 bg-gray-50 rounded-lg">
              <div class="flex items-start">
                <input
                  id="agreeTerms"
                  v-model="agreeTerms"
                  type="checkbox"
                  class="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label for="agreeTerms" class="text-sm text-gray-700">
                  Accetto le
                  <a
                    href="/terms-conditions"
                    target="_blank"
                    class="text-indigo-600 underline hover:text-indigo-800"
                  >
                    Condizioni Generali
                  </a>
                </label>
              </div>

              <div class="flex items-start">
                <input
                  id="agreeInternalRegulations"
                  v-model="agreeInternalRegulations"
                  type="checkbox"
                  class="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label for="agreeInternalRegulations" class="text-sm text-gray-700">
                  Accetto il
                  <a
                    href="/internal-regulations"
                    target="_blank"
                    class="text-indigo-600 underline hover:text-indigo-800"
                  >
                    Regolamento Interno
                  </a>
                </label>
              </div>

              <div class="flex items-start">
                <input
                  id="agreePrivacyPolicy"
                  v-model="agreePrivacyPolicy"
                  type="checkbox"
                  class="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label for="agreePrivacyPolicy" class="text-sm text-gray-700">
                  Accetto l'
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    class="text-indigo-600 underline hover:text-indigo-800"
                  >
                    Informativa sulla Privacy
                  </a>
                </label>
              </div>
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="flex justify-between mt-8 pt-6 border-t">
            <button
              v-if="currentStep > 0"
              type="button"
              class="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              @click="previousStep"
            >
              Indietro
            </button>
            <div v-else></div>

            <button
              v-if="currentStep < 2"
              type="button"
              :disabled="!canProceed"
              :class="!canProceed ? 'opacity-50 cursor-not-allowed' : ''"
              class="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              @click="nextStep"
            >
              Continua
            </button>

            <button
              v-if="currentStep === 2"
              type="button"
              :disabled="!canPay"
              :class="!canPay ? 'opacity-50 cursor-not-allowed' : ''"
              class="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              @click="submitOrder"
            >
              Procedi al Pagamento
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import Footer from "@/Pages/Home/Shared/Footer.vue";
import Header from "@/Pages/Home/Shared/Header.vue";
import PoolMap from "@/Pages/Home/Checkout/PoolMap.vue";
import NumberInput from "@/Pages/Home/Checkout/partials/NumberInput.vue";
import { isSafari } from "@/Shared/helper";
import { Head, Link } from "@inertiajs/inertia-vue3";
import { router } from "@inertiajs/vue3";
import axios from "axios";
import moment from "moment";
import { DatePicker } from "v-calendar";

export default {
  components: {
    Header,
    Footer,
    PoolMap,
    DatePicker,
    NumberInput,
    Head,
    Link,
  },
  props: {
    prices: Object,
    seasonDates: Object,
    poolStatus: Object,
  },
  data() {
    return {
      currentStep: 0,
      steps: ['Data', 'Selezione', 'Conferma'],
      selectedDate: null,
      bookingType: null, // 'tickets' or 'umbrellas'
      adultQty: 0,
      kidsQty: 0,
      totalAdults: 0,
      totalKids: 0,
      selectedStations: [],
      busy: [],
      total: 0,
      agreeTerms: false,
      agreeInternalRegulations: false,
      agreePrivacyPolicy: false,
    };
  },
  computed: {
    returnUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('returnUrl') || '/';
    },
    minDate() {
      if (!this.seasonDates) return new Date();
      const start = new Date(this.seasonDates.start_date);
      const now = new Date();
      const cutoffToday = new Date();
      cutoffToday.setHours(this.seasonDates.cutoff_time || 15, 0, 0, 0);

      if (now >= start && now >= cutoffToday) {
        const t = new Date(now);
        t.setDate(t.getDate() + 1);
        return t;
      }
      return now >= start ? now : start;
    },
    maxDate() {
      return this.seasonDates ? new Date(this.seasonDates.end_date) : new Date();
    },
    disabledDates() {
      if (!this.seasonDates) return [];
      const year = new Date().getFullYear();
      const start = new Date(this.seasonDates.start_date);
      const end = new Date(this.seasonDates.end_date);

      const beforeStart = new Date(start);
      beforeStart.setDate(beforeStart.getDate() - 1);
      const afterEnd = new Date(end);
      afterEnd.setDate(afterEnd.getDate() + 1);

      return [
        { start: new Date(year, 0, 1), end: beforeStart },
        { start: afterEnd, end: new Date(year, 11, 31) },
      ];
    },
    canProceed() {
      if (this.currentStep === 0) return this.selectedDate !== null;
      if (this.currentStep === 1) {
        if (this.bookingType === 'tickets') {
          return this.adultQty > 0 || this.kidsQty > 0;
        }
        if (this.bookingType === 'umbrellas') {
          return this.selectedStations.length > 0;
        }
        return false;
      }
      return true;
    },
    canPay() {
      return this.total > 0 && this.agreeTerms && this.agreeInternalRegulations && this.agreePrivacyPolicy;
    },
  },
  watch: {
    selectedDate(newDate) {
      if (newDate) {
        this.fetchBusyStations(newDate);
      }
    },
  },
  methods: {
    isSafari,
    formatDate(date) {
      return date ? moment(date).format('DD MMMM YYYY') : '';
    },
    nextStep() {
      if (this.canProceed && this.currentStep < 2) {
        this.currentStep++;
      }
    },
    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    selectUmbrellas() {
      this.bookingType = 'umbrellas';
      // Reset tickets
      this.adultQty = 0;
      this.kidsQty = 0;
      this.totalAdults = 0;
      this.totalKids = 0;
      this.updateTotal();
    },
    calculateTotalAdults(val) {
      this.adultQty = Number.parseInt(val);
      this.totalAdults = this.prices.adults * this.adultQty;
      this.updateTotal();
    },
    calculateTotalKids(val) {
      this.kidsQty = Number.parseInt(val);
      this.totalKids = this.prices.kids * this.kidsQty;
      this.updateTotal();
    },
    updateStations(stations) {
      this.selectedStations = stations;
      this.updateTotal();
    },
    updateTotal() {
      const totalUmbrellas = this.selectedStations
        .filter(s => s.type === 'umbrella')
        .length * this.prices.umbrella;

      const totalStations = this.selectedStations
        .filter(s => s.type === 'station')
        .reduce((sum, s) => {
          return sum + this.prices.station +
            s.extraSunbedCount * this.prices.extra_bed +
            s.extraAdultCount * this.prices.adults;
        }, 0);

      this.total = this.totalKids + this.totalAdults + totalUmbrellas + totalStations;
    },
    getStationPrice(station) {
      if (station.type === 'umbrella') {
        return this.prices.umbrella;
      }
      return this.prices.station +
        station.extraSunbedCount * this.prices.extra_bed +
        station.extraAdultCount * this.prices.adults;
    },
    async fetchBusyStations(date) {
      if (!date) return;
      try {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        const response = await axios.get(`/busy?date=${formattedDate}`);
        this.busy = response.data.data.busy;
      } catch (error) {
        console.error('Error fetching busy stations:', error);
      }
    },
    submitOrder() {
      router.post('/stripe/session', {
        kidsQty: this.kidsQty,
        adultQty: this.adultQty,
        selectedDate: moment(this.selectedDate).format('YYYY-MM-DD'),
        stations: this.selectedStations,
        _token: this.$page.props.csrf_token,
      });
    },
  },
};
</script>
